import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import Portal from "components/Portal";
import { EVENT_IOS_4K_READY, type Ios4kReadyDetail } from "../ios4kSave";
import { prepareImageForShare, shareOrDownloadImage, revokePreparedImage, preparedImageFormat, type PreparedImage } from "../shareImage";
import { getFilenameFromUrl } from "../utils";

type Status = "preparing" | "ready" | "error";

/**
 * REF-5796 (Option A) — global iOS "tap-to-save" prompt for 4K downloads.
 *
 * A 4K image is delivered asynchronously (WebSocket / already-4K round-trip), with no live user
 * gesture, so iOS blocks any save. The delivery points dispatch `EVENT_IOS_4K_READY`; this prompt
 * catches it, pre-fetches + decodes the image (so the save can run synchronously), and shows a Save
 * button. The button tap IS a fresh gesture → `navigator.share` opens the native Save to Photos/Files
 * sheet. Mounted once globally (AuthorizationPage); only ever shown on iOS (the event only fires there).
 */
const Ios4kDownloadPrompt: React.FC = () => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<Ios4kReadyDetail | null>(null);
  const [status, setStatus] = useState<Status>("preparing");
  const preparedRef = useRef<PreparedImage | null>(null);
  // Guards against a stale prepare (a newer 4K event) resolving over a newer one.
  const requestIdRef = useRef(0);

  const dismiss = useCallback(() => {
    revokePreparedImage(preparedRef.current);
    preparedRef.current = null;
    setDetail(null);
  }, []);

  useEffect(() => {
    const onReady = (event: Event) => {
      const next = (event as CustomEvent<Ios4kReadyDetail>).detail;
      if (!next?.resultUrl) return;

      revokePreparedImage(preparedRef.current);
      preparedRef.current = null;
      setDetail(next);
      setStatus("preparing");

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      void prepareImageForShare(next.resultUrl).then((prepared) => {
        // A newer event superseded this one — discard.
        if (requestIdRef.current !== requestId) {
          revokePreparedImage(prepared);

          return;
        }

        if (prepared) {
          preparedRef.current = prepared;
          setStatus("ready");
        } else {
          setStatus("error");
        }
      });
    };

    window.addEventListener(EVENT_IOS_4K_READY, onReady as EventListener);

    return () => {
      window.removeEventListener(EVENT_IOS_4K_READY, onReady as EventListener);
      revokePreparedImage(preparedRef.current);
      preparedRef.current = null;
    };
  }, []);

  const handleSave = useCallback(() => {
    const prepared = preparedRef.current;
    if (!prepared || !detail) return;

    const format = preparedImageFormat(prepared);
    const base = (detail.fileName || getFilenameFromUrl(detail.resultUrl, format)).replace(/\.[^/.]+$/, "");

    if (shareOrDownloadImage(prepared, format, base)) {
      dismiss();
    } else {
      setStatus("error");
    }
  }, [detail, dismiss]);

  if (!detail) return null;

  return (
    <Portal>
      {/* Mirrors DownloadProgressPopup: bottom-right toast, above the z-[99999] detail modals. */}
      <div className="fixed bottom-4 right-4 z-[100001] w-[400px] bg-black-300 border border-black-300 rounded-lg shadow-lg flex flex-col gap-3 p-3">
        <div className="flex gap-3">
          <img src={preparedRef.current?.objectUrl || detail.resultUrl} alt="" className="w-12 h-12 object-cover rounded flex-shrink-0" />
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-white text-h5">{status === "error" ? t("common:download_menu.save_4k_error") : t("common:download_menu.save_4k_title")}</span>
            {status !== "error" && <span className="text-white text-bodySm">{t("common:download_menu.save_4k_subtitle")}</span>}
          </div>
          <button type="button" aria-label={t("common:download_menu.save_4k_button")} onClick={dismiss} className="text-white/70 hover:text-white flex-shrink-0 self-start">
            <X size={18} />
          </button>
        </div>

        {status !== "error" && (
          <button
            type="button"
            disabled={status !== "ready"}
            onClick={handleSave}
            className="w-full rounded-md bg-trueWhite text-trueBlack font-medium py-2 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "ready" ? t("common:download_menu.save_4k_button") : t("common:download_menu.save_4k_preparing")}
          </button>
        )}
      </div>
    </Portal>
  );
};

export default Ios4kDownloadPrompt;
